"use client";

import { useState } from "react";
import { formatPrice } from "@/lib/format";
import { luxuryImagesByBoutique } from "@/lib/luxury-images";
import { useCart } from "@/lib/cart-context";

interface Promo {
  badge: string;
  badgeType: "percent" | "edition";
  category: "Frais" | "Épicerie" | "Beauté" | "High-Tech" | "Cave";
  detail: string;
  name: string;
  oldPrice: number;
  newPrice: number;
  image: string;
}

const POOL = {
  frais: luxuryImagesByBoutique["frais-marche"] ?? [],
  epicerie: luxuryImagesByBoutique["epicerie-fine"] ?? [],
  epicerieMonde: luxuryImagesByBoutique["epicerie-monde"] ?? [],
  beaute: luxuryImagesByBoutique["beaute-parfums"] ?? [],
  hightech: luxuryImagesByBoutique["hightech-electro"] ?? [],
  cave: luxuryImagesByBoutique["cave-exception"] ?? [],
};

const PROMOS: Promo[] = [
  { badge: "-23%", badgeType: "percent", category: "Frais", detail: "Fruits & légumes bio", name: "Panier du Terroir", oldPrice: 24000, newPrice: 18500, image: POOL.frais[0] },
  { badge: "-22%", badgeType: "percent", category: "Frais", detail: "Pêche du jour · 1,2 kg", name: "Dorade de l'Atlantique", oldPrice: 16500, newPrice: 12800, image: POOL.frais[1] },
  { badge: "-19%", badgeType: "percent", category: "Frais", detail: "Halal · 800 g · 28 jours", name: "Côte de Bœuf Maturée", oldPrice: 26000, newPrice: 21000, image: POOL.frais[2] },
  { badge: "Édition Limitée", badgeType: "edition", category: "Épicerie", detail: "Caviar · Champagne · Truffe", name: "Coffret Grande Maison", oldPrice: 210000, newPrice: 165000, image: POOL.epicerie[3] },
  { badge: "-23%", badgeType: "percent", category: "Épicerie", detail: "Vieilli 2 ans · Import Iran", name: "Riz Basmati Royal 5kg", oldPrice: 11500, newPrice: 8900, image: POOL.epicerieMonde[4] },
  { badge: "-19%", badgeType: "percent", category: "Épicerie", detail: "Extra vierge · 3×75cl", name: "Huile d'Olive Trio", oldPrice: 18000, newPrice: 14500, image: POOL.epicerieMonde[5] },
  { badge: "-20%", badgeType: "percent", category: "Beauté", detail: "Parfum + Soin J'adore", name: "Coffret Beauté Dior", oldPrice: 115000, newPrice: 92000, image: POOL.beaute[6] },
  { badge: "-15%", badgeType: "percent", category: "Beauté", detail: "Mademoiselle · EDP 100ml", name: "Coffret Chanel Coco", oldPrice: 162000, newPrice: 138000, image: POOL.beaute[7] },
  { badge: "-9%", badgeType: "percent", category: "High-Tech", detail: "256 Go · Titanium Black", name: "Galaxy S25 Ultra", oldPrice: 825000, newPrice: 749000, image: POOL.hightech[8] },
  { badge: "-7%", badgeType: "percent", category: "High-Tech", detail: "256 Go · Titane Naturel", name: "iPhone 15 Pro Max", oldPrice: 945000, newPrice: 879000, image: POOL.hightech[9] },
  { badge: "-14%", badgeType: "percent", category: "High-Tech", detail: "4K · Smart TV · 2025", name: 'Samsung TV OLED 65"', oldPrice: 1150000, newPrice: 985000, image: POOL.hightech[10] },
  { badge: "-17%", badgeType: "percent", category: "High-Tech", detail: "No Frost · Inox & Or", name: "Frigo LG American", oldPrice: 825000, newPrice: 685000, image: POOL.hightech[11] },
  { badge: "-20%", badgeType: "percent", category: "Cave", detail: "Bordeaux · Bourgogne · Rhône", name: "Cave d'Exception 6 btl", oldPrice: 168000, newPrice: 135000, image: POOL.cave[12] },
];

const FILTERS = ["Tous", "Frais & Marché", "Épicerie Fine", "Beauté", "High-Tech", "Cave"] as const;
type Filter = typeof FILTERS[number];

const FILTER_MAP: Record<Exclude<Filter, "Tous">, Promo["category"]> = {
  "Frais & Marché": "Frais",
  "Épicerie Fine": "Épicerie",
  "Beauté": "Beauté",
  "High-Tech": "High-Tech",
  "Cave": "Cave",
};

export function PromotionsSection() {
  const [filter, setFilter] = useState<Filter>("Tous");
  const { addItem } = useCart();

  const filtered =
    filter === "Tous"
      ? PROMOS
      : PROMOS.filter((p) => p.category === FILTER_MAP[filter]);

  return (
    <section id="promotions" className="relative bg-cream py-24 md:py-32 border-t border-ink/8">
      <div className="mx-auto max-w-7xl px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="ornament-line mb-6">
              <span className="text-gold text-[11px] tracking-[0.35em] uppercase">
                Sélection de la semaine
              </span>
            </div>
            <h2 className="font-display text-ink text-4xl md:text-6xl leading-[1.05]">
              Promotions
              <br />
              <span className="gold-text italic">d&apos;exception.</span>
            </h2>
          </div>
          <p className="text-ink/55 font-serif italic text-base">
            Renouvelé chaque mardi
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-10">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase border transition-all ${
                filter === f
                  ? "bg-ink text-cream border-ink"
                  : "border-ink/15 text-ink/70 hover:border-ink/40 hover:text-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Promo grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((p, idx) => (
            <article
              key={`${p.name}-${idx}`}
              className="group relative bg-white border border-ink/8 rounded-xl overflow-hidden hover:luxury-shadow transition-all duration-500 hover:-translate-y-0.5 flex flex-col"
            >
              {/* Visual */}
              <div
                className="relative aspect-[4/3] overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, var(--parchment) 0%, var(--cream) 100%)",
                }}
              >
                {p.image && (
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="eager"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {/* Badge */}
                <div className="absolute top-3 left-3 z-10">
                  <span
                    className={`px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold ${
                      p.badgeType === "edition"
                        ? "bg-bordeaux text-cream"
                        : "bg-gold text-ink"
                    }`}
                  >
                    {p.badge}
                  </span>
                </div>
                {/* Category */}
                <div className="absolute bottom-3 left-3 z-10">
                  <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-medium bg-white/90 text-ink/70 backdrop-blur">
                    {p.category}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 flex flex-col p-4">
                <p className="text-ink/45 text-[11px] leading-relaxed">
                  {p.detail}
                </p>
                <h4 className="mt-1 font-serif text-ink text-[15px] leading-snug">
                  {p.name}
                </h4>
                <div className="mt-3 pt-3 border-t border-ink/8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-ink/35 text-xs line-through">
                      {formatPrice(p.oldPrice)}
                    </span>
                    <span className="font-display text-bordeaux text-lg leading-none">
                      {formatPrice(p.newPrice)}
                    </span>
                  </div>
                  <button
                    aria-label={`Ajouter ${p.name} au panier`}
                    onClick={() =>
                      addItem({
                        id: `promo-${p.name}-${idx}`,
                        name: p.name,
                        price: p.newPrice,
                        image: p.image,
                        boutiqueName: p.category,
                      })
                    }
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-full border border-ink/15 text-ink/70 hover:bg-gold hover:text-ink hover:border-gold transition-all text-[10px] tracking-[0.2em] uppercase"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14"></path>
                      <path d="M12 5v14"></path>
                    </svg>
                    Ajouter
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex justify-center">
          <a
            href="#chef-picks"
            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-ink/30 text-ink hover:bg-ink hover:text-cream transition-colors tracking-[0.2em] uppercase text-sm"
          >
            Voir le choix du chef
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14"></path>
              <path d="m19 12-7 7-7-7"></path>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
